import React from 'react'
import injectSheet from 'react-jss'
import styles from './contribution-form.styles'
import classNames from 'classnames'
import { Formik } from 'formik'
import * as Yup from 'yup'
import FormTextArea from 'pobble-components/dist/form-elements/form-text-area'
import Button from 'pobble-components/dist/button'
import ArrowIcon from 'react-icons/lib/fa/caret-right'
import PaperclipIcon from 'react-icons/lib/fa/paperclip'
import TextIcon from 'react-icons/lib/fa/align-left'
import WorkIcon from 'react-icons/lib/fa/file-text-o'
import FormFile from 'pobble-components/dist/form-elements/form-file'
import { MyAvatar } from 'pobble-components/dist/pobble-avatar'
import Tooltip from 'pobble-components/dist/tooltip'

const ContributionForm = ({
  classes,
  onSubmit,
  pending,
  posting,
  isConcluded,
  isClosed,
  onShowTemplates,
  onHideTemplates,
  areTemplatesVisible,
  onShowWorks,
  onHideWorks,
  areWorksVisible,
  works
}) => {
  return (
    <Formik
      onSubmit={(values, { resetForm }) => {
        onSubmit(values)
        resetForm()
      }}
      initialValues={{
        contribution: '',
        workIds: []
      }}
      validationSchema={Yup.object().shape({
        contribution: Yup.string().required('Please provide a contribution')
      })}>
      {formikProps => {
        const {
          values,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        } = formikProps
        return (
          <form
            id="metrics__moderation-af-discussion-form"
            onSubmit={handleSubmit}
            className={classNames(classes.container, {
              [classes.pending]: pending
            })}>
            <div className={classes.area}>
              <MyAvatar className={classes.avatar} />
              <FormTextArea
                name="contribution"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.contribution}
                className={classes.textarea}
                placeholder={
                  !isClosed
                    ? 'Eg. This pupil is working as expected.'
                    : 'This discussion is now closed.'
                }
                disabled={isClosed}
              />
            </div>
            <div className={classes.actions}>
              <div>
                <Tooltip
                  content={
                    values.fileName
                      ? values.fileName + ' will be added to my contribution'
                      : 'Attach a file to my contribution'
                  }>
                  <FormFile
                    accept=".jpg,.png,.doc,.docx,.xls,.xlsx,.pdf,.zip,.gif,.csv"
                    className={classNames(classes.uploader, {
                      [classes.uploadReady]: values.file
                    })}
                    disabled={isClosed}
                    primary
                    onChange={files => {
                      files[0] && setFieldValue('fileName', files[0].name)
                      return setFieldValue('file', files[0])
                    }}>
                    <span
                      className={classNames(classes.upload, {
                        [classes.uploadDisabled]: isClosed
                      })}>
                      <PaperclipIcon />
                    </span>
                  </FormFile>
                </Tooltip>
                <Tooltip content={'Pick a template'}>
                  {!areTemplatesVisible && (
                    <Button
                      small
                      simple
                      disabled={isClosed}
                      action={() => {
                        onShowTemplates()
                      }}>
                      <TextIcon />
                    </Button>
                  )}
                  {areTemplatesVisible && (
                    <Button
                      small
                      primary
                      disabled={isClosed}
                      action={onHideTemplates}>
                      <TextIcon />
                    </Button>
                  )}
                </Tooltip>
                <Tooltip content={'Reference a work'}>
                  {!areWorksVisible && (
                    <Button
                      small
                      simple
                      disabled={isClosed}
                      action={onShowWorks}>
                      <WorkIcon />
                    </Button>
                  )}
                  {areWorksVisible && (
                    <Button
                      small
                      primary
                      disabled={isClosed}
                      action={onHideWorks}>
                      <WorkIcon />
                    </Button>
                  )}
                </Tooltip>
              </div>
              <div>
                <Button
                  simple
                  className={classNames(classes.cancel, {
                    [classes.visible]: values.contribution.length
                  })}
                  action={() => setFieldValue('contribution', '')}>
                  Cancel
                </Button>
                <Button
                  className={classes.button}
                  disabled={posting}
                  type="submit">
                  {`${
                    !isConcluded
                      ? 'Submit contribution'
                      : 'Reopen discussion and submit contribution'
                  }`}{' '}
                  <ArrowIcon />
                </Button>
              </div>
            </div>
            {areTemplatesVisible && (
              <ul className={classes.templates}>
                <li
                  className={classes.template}
                  onClick={() => {
                    onHideTemplates()
                    return setFieldValue(
                      'contribution',
                      values.contribution +
                        'Across the collection the pupil has demonstrated effective application of...'
                    )
                  }}>
                  Across the collection the pupil has demonstrated effective
                  application of...
                </li>
                <li
                  className={classes.template}
                  onClick={() => {
                    onHideTemplates()
                    return setFieldValue(
                      'contribution',
                      values.contribution +
                        'Across the collection the pupil is beginning to demonstrate good use of...'
                    )
                  }}>
                  Across the collection the pupil is beginning to demonstrate
                  good use of...
                </li>
                <li
                  className={classes.template}
                  onClick={() => {
                    onHideTemplates()
                    return setFieldValue(
                      'contribution',
                      values.contribution +
                        'Within this piece the pupil has demonstrated effective application of ... in line with the writing purpose'
                    )
                  }}>
                  Within this piece the pupil has demonstrated effective
                  application of ... in line with the writing purpose
                </li>
                <li
                  className={classes.template}
                  onClick={() => {
                    onHideTemplates()
                    return setFieldValue(
                      'contribution',
                      values.contribution +
                        'The writing purpose is clear and maintained, the pupil has achieved this by...'
                    )
                  }}>
                  The writing purpose is clear and maintained, the pupil has
                  achieved this by...
                </li>
                <li
                  className={classes.template}
                  onClick={() => {
                    onHideTemplates()
                    return setFieldValue(
                      'contribution',
                      values.contribution +
                        'Following our discussions I feel that the most appropriate next step is for this pupil to...'
                    )
                  }}>
                  Following our discussions I feel that the most appropriate
                  next step is for this pupil to...
                </li>
                <li
                  className={classes.template}
                  onClick={() => {
                    onHideTemplates()
                    return setFieldValue(
                      'contribution',
                      values.contribution +
                        'The discussions have reconfirmed my thinking that...'
                    )
                  }}>
                  The discussions have reconfirmed my thinking that...
                </li>
                <li
                  className={classes.template}
                  onClick={() => {
                    onHideTemplates()
                    return setFieldValue(
                      'contribution',
                      values.contribution +
                        'Having discussed the collection I would reconsider a revised judgement to...'
                    )
                  }}>
                  Having discussed the collection I would reconsider a revised
                  judgement to...
                </li>
              </ul>
            )}
            {areWorksVisible && (
              <ul className={classes.templates}>
                {works &&
                  works.map((work, index) => (
                    <li
                      key={index}
                      id={work.id}
                      className={classNames(classes.thumbnail, {
                        [classes.selected]: values.workIds.indexOf(work.id) > -1
                      })}
                      onClick={() => setFieldValue('workIds', [work.id])}>
                      <div>
                        <span
                          style={{
                            backgroundImage: `url("${work.media &&
                              work.media[0] &&
                              work.media[0].thumb_url}")`
                          }}>
                          <strong>{index + 1}</strong>
                        </span>
                      </div>
                    </li>
                  ))}
              </ul>
            )}
          </form>
        )
      }}
    </Formik>
  )
}

export default injectSheet(styles)(ContributionForm)
